import { Form, Modal, Row } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { IAmlz, ISubj, Ic5 } from 'types/amlz';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import TableComponent from 'components/atoms/TableComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import { IcisStaty } from 'services/cisStaty';
import { amlzC2osobyDetailGet } from 'services/spravaEmitenta/amlzData';

import { EAnoNe } from 'enums/anoNe';

import c5PepSelect from 'data/codebooks/amlz/c5jePepSelect';

import getLanguageObject from 'utils/getLanguageObject';

import C5PridatOsobu from './C5PridatOsobu';

interface IC5 {
    C5data?: Ic5;
    countries?: IcisStaty[];
    prevStep: () => void;
    nextStep: () => void;
    SubmitFunc: <T extends keyof IAmlz['amlz']>(key: T, values: IAmlz['amlz'][T]) => void;
    isC6edit: string;
    setSaving: React.Dispatch<React.SetStateAction<boolean>>;
    idZadosti: number;
    isLegalEntity: boolean;
}

const C5: React.FC<IC5> = ({
    C5data,
    countries,
    prevStep,
    nextStep,
    SubmitFunc,
    isC6edit,
    setSaving,
    idZadosti,
    isLegalEntity
}) => {
    const { info } = Modal;
    const { language } = useContext(Context);
    const [form] = Form.useForm();
    const [personData, setPersonData] = useState<ISubj | undefined>();
    const [submitPersonData, setSubmitPersonData] = useState<ISubj | null>();
    const [dotaznikOnly, setDotaznikOnly] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [subjIndex, setSubjIndex] = useState<number>(-1);
    const [deletePerson, setDeletePerson] = useState<number>(-1);
    const [showTable, setShowTable] = useState<EAnoNe | undefined>(C5data?.ma);

    const handleSelectChange = (name: string, value: EAnoNe) => {
        form.setFieldsValue({
            [name]: value
        });
        setShowTable(value);
    };

    const onFinish = () => {
        SubmitFunc('c5', {
            ma: showTable ? showTable : EAnoNe.NE,
            subj: showTable === EAnoNe.ANO && C5data ? C5data.subj : []
        });
        nextStep();
    };

    useEffect(() => {
        setShowTable(C5data?.ma);
        form.setFieldsValue({
            ma: C5data?.ma
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [C5data]);

    useEffect(() => {
        !personData && setDotaznikOnly(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personData]);

    useEffect(() => {
        if (submitPersonData) {
            const subj = C5data?.subj && C5data.subj.length > 0 ? JSON.parse(JSON.stringify(C5data?.subj)) : [];
            const subjToUpdate = [...subj];
            if (subjIndex > -1) {
                subjToUpdate[subjIndex] = submitPersonData;
            } else {
                subjToUpdate.push(submitPersonData);
            }
            setSaving(true);
            SubmitFunc('c5', {
                ma: showTable ? showTable : EAnoNe.ANO,
                subj: subjToUpdate
            });

            setSubjIndex(-1);
            setPersonData(undefined);
            setSubmitPersonData(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitPersonData]);

    useEffect(() => {
        const delPerson = () => {
            const subj = JSON.parse(JSON.stringify(C5data?.subj));
            subj.splice(deletePerson, 1);
            setSaving(true);
            SubmitFunc('c5', {
                ma: showTable ? showTable : EAnoNe.ANO,
                subj: subj
            });
            setDeletePerson(-1);
        };

        if (deletePerson > -1) {
            if (C5data?.subj![deletePerson].c2osobaoid) {
                setLoading(true);
                amlzC2osobyDetailGet(idZadosti, C5data?.subj[deletePerson].c2osobaoid!)
                    .then(resp => {
                        resp.pep
                            ? info({
                                  title: <TranslateText>UPOZORNENI</TranslateText>,
                                  content: `${getLanguageObject(language).AMLZ_C5_PEP_DEL_ERR}`,
                                  okText: <TranslateText>OK</TranslateText>,
                                  onOk: () => {}
                              })
                            : delPerson();
                    })
                    .catch(() => {
                        delPerson();
                    })
                    .finally(() => {
                        setLoading(false);
                        setDeletePerson(-1);
                    });
            } else {
                delPerson();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deletePerson]);

    const columns = [
        {
            title: <TranslateText>JMENO</TranslateText>,
            dataIndex: 'jmeno',
            key: 'jmeno',
            render: (text: ISubj, row: ISubj) => (
                <p
                    style={{
                        margin: 0
                    }}
                >
                    {' '}
                    {text + ' ' + row.prijmeni}{' '}
                </p>
            )
        },
        {
            title: <TranslateText>FORMULARE_ADRESA_POBYTU</TranslateText>,
            dataIndex: 'adma',
            key: 'adma',
            render: (_text: ISubj, row: ISubj) => (
                <span>{row.adma + ', ' + row.mema + ', ' + row.pscm + ', ' + row.kost}</span>
            )
        },
        {
            width: 80,
            render: (_text: ISubj, _row: ISubj, index: number) => (
                <AnchorComponent
                    id={`C5UpravitBtn${index}`}
                    key={index}
                    onClick={() => {
                        setPersonData(C5data?.subj ? C5data.subj[index] : undefined);
                        setSubjIndex(index);
                    }}
                >
                    <TranslateText>BUTTON_UPRAVIT</TranslateText>
                </AnchorComponent>
            )
        },
        {
            width: 150,
            render: (_text: ISubj, _row: ISubj, index: number) => (
                <AnchorComponent
                    id={`C5UpravitDotazBtn${index}`}
                    key={index}
                    onClick={() => {
                        setPersonData(C5data?.subj ? C5data.subj[index] : undefined);
                        setSubjIndex(index);
                        setDotaznikOnly(true);
                    }}
                >
                    <TranslateText>BUTTON_UPRAVIT_DOTAZNIK</TranslateText>
                </AnchorComponent>
            )
        },
        {
            width: 80,
            render: (_text: ISubj, _row: ISubj, index: number) => (
                <AnchorComponent id={`C5OdebratBtn${index}`} key={index} onClick={() => setDeletePerson(index)}>
                    <TranslateText>ODEBRAT</TranslateText>
                </AnchorComponent>
            )
        }
    ];
    return !personData ? (
        <LoadingWrapper loading={loading}>
            <FormWrapper onFinish={onFinish} outForm={form}>
                <Paragraph
                    text={'FORMULARE_AMLZ_C5_SUBTITLE'}
                    style={{
                        display: 'block'
                    }}
                />
                <Paragraph
                    text={'FORMULARE_AMLZ_C5_TEXTA'}
                    style={{
                        display: 'block'
                    }}
                />
                <Paragraph
                    text={'FORMULARE_AMLZ_C5_TEXTB'}
                    style={{
                        display: 'block'
                    }}
                />
                <Paragraph
                    text={'FORMULARE_AMLZ_C5_TEXTB1'}
                    style={{
                        display: 'block'
                    }}
                />
                <Paragraph
                    text={'FORMULARE_AMLZ_C5_TEXTB2'}
                    style={{
                        display: 'block'
                    }}
                />
                <Paragraph
                    text={'FORMULARE_AMLZ_C5_TEXTB3'}
                    style={{
                        display: 'block',
                        paddingBottom: 20
                    }}
                />
                <SelectComponent
                    isFormItem={true}
                    name="ma"
                    labelParagraph={
                        isLegalEntity
                            ? 'FORMULARE_AMLZ_C5_TEXT_PROHLASENI_LEGALENTIY'
                            : 'FORMULARE_AMLZ_C5_TEXT_PROHLASENI'
                    }
                    defaultValue={C5data?.ma ? C5data.ma : undefined}
                    onChange={value => handleSelectChange('ma', value)}
                    data={c5PepSelect}
                />
                {showTable === EAnoNe.ANO && (
                    <TableComponent
                        locale={{
                            emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                        }}
                        style={{
                            margin: '30px 0px'
                        }}
                        rowClassName={row =>
                            row.dotaznikpep && typeof row.dotaznikpep.zrizovatelmaj === 'boolean' ? '' : 'tableRowRed'
                        }
                        rowKey={(_, index) => index || 0}
                        columns={columns}
                        dataSource={C5data?.subj}
                        pagination={false}
                        footer={() => (
                            <AnchorComponent
                                id="c5PridatOsobuBtn"
                                style={{
                                    textAlign: 'center',
                                    display: 'block'
                                }}
                                onClick={() => {
                                    setPersonData({});
                                    setSubjIndex(-1);
                                }}
                            >
                                <TranslateText>PRIDAT_OSOBU</TranslateText>
                            </AnchorComponent>
                        )}
                    />
                )}
                {isC6edit ? (
                    <Row className="button-row">
                        <ButtonComponent
                            buttontext="POKRACOVAT_NA_ZHRNUTI"
                            id="c5PrehledBtn"
                            type="primary"
                            htmlType="button"
                            onClick={onFinish}
                            disabled={showTable === EAnoNe.ANO && C5data?.subj ? C5data.subj.length < 1 : false}
                        />
                    </Row>
                ) : (
                    <Row className="button-row">
                        <ButtonComponent buttontext="ZPET" onClick={() => prevStep()} id="c5ZpetBtn" type="default" />
                        <ButtonComponent
                            extramessage={
                                (C5data?.subj || []).filter(
                                    e => !e.dotaznikpep || typeof e.dotaznikpep.zrizovatelmaj !== 'boolean'
                                ).length > 0
                                    ? 'C5_DOTAZNIK_ERROR'
                                    : ''
                            }
                            id="c5SubmitBtn"
                            type="primary"
                            htmlType="submit"
                            buttontext="POKRACOVAT"
                            disabled={
                                showTable === EAnoNe.ANO && C5data?.subj
                                    ? C5data.subj.length < 1 ||
                                      C5data?.subj?.filter(
                                          e => !e.dotaznikpep || typeof e.dotaznikpep.zrizovatelmaj !== 'boolean'
                                      ).length > 0
                                    : false
                            }
                        />
                    </Row>
                )}
            </FormWrapper>
        </LoadingWrapper>
    ) : (
        <C5PridatOsobu
            dotaznikOnly={dotaznikOnly}
            setSubmitPersonData={setSubmitPersonData}
            personData={personData}
            setPersonData={setPersonData}
            countries={countries}
        />
    );
};

export default C5;
